import styled from 'styled-components';
import { Box, Text } from '@rsos/base-components';

export const StyledTranscriptViewer = styled(Box)`
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.heavyLineOutline};
  border-radius: ${({ isTextToVoiceSelected }) =>
    isTextToVoiceSelected ? '5px 5px 0 0' : '5px'};
  border-bottom-width: ${({ isTextToVoiceSelected }) =>
    isTextToVoiceSelected ? '0' : '1px'};
  flex: 1;
  padding: 8px 12px;
  overflow-x: hidden;
  overflow-y: auto;
  position: relative;
`;

export const StyledTranscriptTokens = styled(Box)`
  display: flex;
  flex-grow: 1;
  overflow: auto;
`;

export const StyledTranscriptHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom ${({ pb = 0 }) => pb}
`;

export const StyledTranscriptTitle = styled(Text).attrs(props => ({
  fontWeight: 'bold',
  size: 'normal',
  color: 'primaryText',
  marginBottom: props.noMargin ? '0px' : '16px',
}))``;

const getSpeakerColor = speakerName => {
  const speaker = `${speakerName}`.toLowerCase();
  if (speaker === 'dispatcher') {
    return 'primaryBase';
  } else if (speaker === 'caller') {
    return 'success';
  }
  return 'primaryText';
};

/**
 * @typedef {Object} StyledSpeakerProps
 * @property {string} speakerName - The name of the speaker to determine text color
 */

/**
 * Styled component for displaying speaker names with appropriate colors
 * @type {import('styled-components').StyledComponent<typeof Text, any, StyledSpeakerProps, never>}
 */
export const StyledSpeaker = styled(Text).attrs(({ speakerName }) => ({
  fontWeight: 'bold',
  size: 'normal',
  color: getSpeakerColor(speakerName),
  inline: true,
}))``;

export const StyledTimestamp = styled(Text).attrs({
  fontWeight: 'regular',
  size: 'normal',
  color: 'secondaryText',
  inline: true,
})``;

export const StyledTranscriptState = styled(Text).attrs({
  fontWeight: 'regular',
  size: 'normal',
  color: 'secondaryText',
})``;

export const StyledMessage = styled.div`
  margin-bottom: 16px;
`;

export const StyledToken = styled(Text).attrs({
  fontWeight: 'regular',
  size: 'normal',
  color: 'primaryText',
})`
  margin-top: 4px;
`;

export const StyledTokenContainer = styled(Box).attrs({
  horizontal: true,
  mt: '4px',
  // colors and weight for the quotes on either side of the spans
  fontWeight: 'regular',
  size: 'normal',
  color: 'primaryText',
})`
  /* without 'display: block;' Box reverts to flex and ends up causing some descendent items to become flex-items and then they shrink and wrap. You can see it in the "Multiple Words Highlight" storybook story. */
  display: block;
`;

export const StyledTokenRegular = styled(Text).attrs({
  inline: true,
  fontWeight: 'regular',
  size: 'normal',
  color: 'primaryText',
})`
  display: inline;
`;

export const StyledTokenHighlighted = styled(Text).attrs({
  inline: true,
  fontWeight: 'regular',
  size: 'normal',
  color: 'background',
  backgroundColor: 'primaryBase',
})`
  display: inline;
`;

export const TokenWithHighlightsWrapper = styled.div`
  display: inline;
`;

export const StyledLoaderContainer = styled(Box).attrs({
  justify: 'center',
  align: 'center',
})`
  width: 100%;
  min-height: 160px;
  padding: 12px;
  text-align: center;

  /* Override Loader's default fixed sizing (60px wide/tall). */
  > div {
    width: 100%;
    height: auto;
  }

  /* Loader text block: constrain width and allow wrapping. */
  > div > div:last-child {
    max-width: 320px;
    margin-left: auto;
    margin-right: auto;
    white-space: normal;
  }
`;

export const ButtonContainer = styled(Box).attrs({
  horizontal: true,
  flow: 4,
})``;

export const StyledHoverableText = styled(Text).attrs({
  fontWeight: 'regular',
  inline: true,
})`
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.primaryBase};
    text-decoration: underline dotted;
  }
`;

export const PopoverBox = styled(Box).attrs({
  padding: '10px',
  backgroundColor: 'primaryText',
  borderRadius: '5px',
})``;

export const PopoverText = styled(Text).attrs({
  color: 'background',
  size: 'small',
})``;

export const LanguageControlsWrapper = styled(Box).attrs({
  horizontal: true,
  align: 'center',
})`
  margin-top: 8px;
  margin-bottom: 16px;
  width: 100%;
  height: 40px;
  padding: 8px 12px;
  border-radius: 5px;
  background: ${({ theme }) => theme.colors.foreground};

  > *:first-child {
    flex: 1.5;
    margin-right: 16px;
  }

  > *:last-child {
    flex: 1;
  }
`;
